import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import { useTranslation } from "../../../i18n";
import { Link as RouterLink } from "react-router-dom";
import { useTeam } from "../TeamLayout";
import { AspectRatio, Breadcrumbs, Button, Input, Link, Stack } from "@mui/joy";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInspectionListQuery } from "../../../__generated__/types-and-hooks";
import AddIcon from "@mui/icons-material/Add";
import TableView from "./views/TableView";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import InspectionAddModal from "./InspectionAddModal";
import Fuse from "fuse.js";

export default function Inspections() {
  const t = useTranslation();
  const teamCtx = useTeam();
  const inspectionsListQuery = useInspectionListQuery({
    variables: { teamId: teamCtx.team.id, teamKind: teamCtx.team.kind },
  });

  const inspections =
    inspectionsListQuery.data &&
    inspectionsListQuery.data.inspection_list?.inspections;

  const [modal, setModal] = useState<"add_inspection" | null>(null);

  const handleAddInspection = useCallback(() => {
    setModal("add_inspection");
  }, []);

  const [search, setSearch] = useState<string | null>(null);
  const [inspectionsFiltered, setInspectionsFiltered] = useState<
    typeof inspections | undefined
  >(undefined);

  const fuse = useMemo(() => {
    if (!inspections) return null;

    return new Fuse(inspections, {
      keys: [
        { name: "uniqueId", weight: 1 },
        { name: "status", weight: 0.8 },
        { name: "serviceType", weight: 0.5 },
        { name: "serviceDays", weight: 0.3 },
        { name: "inspectorNotes", weight: 0.2 },
        { name: "costMemo", weight: 0.1 },
        { name: "endDate", weight: 0.1 },
        { name: "startDate", weight: 0.1 },
        { name: "supplier.name", weight: 0.1 },
        { name: "agency.name", weight: 0.1 },
      ],
      threshold: 0.2, // 0 is exact, 1 is very fuzzy
      includeScore: true,
    });
  }, [inspections]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setInspectionsFiltered(
        fuse && search && search.trim() !== ""
          ? fuse.search(search.trim()).map((result) => result.item)
          : inspections
      );
    }, 500);

    return () => clearTimeout(handler); // cleanup on every keystroke
  }, [search, inspections, fuse]);

  return (
    <>
      <Stack direction="row">
        <Breadcrumbs
          aria-label="breadcrumbs"
          sx={{
            "--Breadcrumbs-gap": "4px",
            m: 1,
          }}
          size="sm"
        >
          <Link component={RouterLink} color="neutral" to="/">
            <HomeIcon />
          </Link>
          <Link
            component={RouterLink}
            color="neutral"
            to={`/${teamCtx.team.slug}`}
          >
            {teamCtx.team.name}
          </Link>
          <Typography level="title-sm">{t("Inspections")}</Typography>
        </Breadcrumbs>
        {/* <OnlineUsersToggle /> */}
      </Stack>
      <Box sx={{ display: "flex", m: 2, mt: 0 }} gap={1}>
        <Input
          type="text"
          placeholder={t("Search...")}
          startDecorator={<SearchIcon />}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Box sx={{ flex: 1 }}></Box>
        {/* <Button
          variant="outlined"
          color="neutral"
          startDecorator={<TuneIcon />}
          onClick={() => setOpen(true)}
        >
          {t("Filters")}
        </Button> */}
        <Button
          startDecorator={<AddIcon />}
          variant="outlined"
          onClick={handleAddInspection}
        >
          {t("Draft")}
        </Button>
      </Box>
      <Box
        sx={{
          gap: 2,
          px: 2,
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 120px)",
        }}
      >
        {inspections && inspections.length === 0 && (
          <>
            <Typography color="neutral" level="body-md">
              {t(
                "No Inspections yet! Start by adding one manually or connecting to an integration like Anvyl or NetSuite."
              )}
            </Typography>

            <Link
              component={RouterLink}
              to={`/${teamCtx.team.slug}/settings/integrations`}
            >
              {t("Settings/Integrations")}
            </Link>

            <AspectRatio
              variant="plain"
              ratio={1}
              objectFit="contain"
              maxHeight={"50vh"}
              sx={{ bgcolor: "transparent" }}
            >
              <img
                alt={t("No Inspections yet")}
                src="/images/inspections_empty_state.png"
              />
            </AspectRatio>

            <Box
              sx={{
                textAlign: "center",
              }}
            >
              <Button sx={{ mb: 2 }} variant="outlined">
                {t("Add")}
              </Button>
            </Box>
          </>
        )}

        <TableView
          inspections={inspectionsFiltered ?? inspections ?? []}
          loading={inspectionsListQuery.loading}
        />
      </Box>

      <InspectionAddModal
        open={modal === "add_inspection"}
        onClose={() => {
          inspectionsListQuery.refetch();
          setModal(null);
        }}
      />
    </>
  );
}
